import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supplierAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  CubeIcon,
  ShoppingCartIcon,
  BanknotesIcon,
  ClockIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ArrowRightIcon,
  Cog6ToothIcon,
  TruckIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

const ORDER_STATUSES = [
  { value: '', label: 'All Orders' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready', label: 'Ready' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' }
];

const SupplierDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [chartStatusFilter, setChartStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, profileRes, ordersRes, allOrdersRes] = await Promise.all([
        supplierAPI.getStats(),
        supplierAPI.getProfile(),
        supplierAPI.listOrders({ limit: 5 }),
        supplierAPI.listOrders({})
      ]);
      setStats(statsRes.data);
      setProfile(profileRes.data.supplier);
      setRecentOrders(ordersRes.data.orders || []);
      setAllOrders(allOrdersRes.data.orders || []);
    } catch (error) {
      console.error('Load supplier data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (!chartStatusFilter) return allOrders;
    return allOrders.filter(order => order.status === chartStatusFilter);
  }, [allOrders, chartStatusFilter]);

  const chartData = useMemo(() => {
    if (!allOrders.length) return [];

    const now = new Date();
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

    const weeks = [];
    let currentWeek = new Date(twelveMonthsAgo);
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay());

    while (currentWeek <= now) {
      weeks.push(new Date(currentWeek));
      currentWeek.setDate(currentWeek.getDate() + 7);
    }

    const weeklyData = weeks.map((weekStart, index) => {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const weekOrders = filteredOrders.filter(order => {
        const orderDate = new Date(order.placedAt || order.createdAt);
        return orderDate >= weekStart && orderDate <= weekEnd;
      });

      const totalRevenue = weekOrders.reduce((sum, order) => {
        if (order.status !== 'cancelled') {
          return sum + Number(order.supplierAmount || order.totalAmount || 0);
        }
        return sum;
      }, 0);

      const weekLabel = weekStart.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });

      return {
        week: weekLabel,
        orders: weekOrders.length,
        revenue: Math.round(totalRevenue)
      };
    });

    return weeklyData.map((d, i) => ({
      ...d,
      weekDisplay: i % 4 === 0 ? d.week : ''
    }));
  }, [allOrders, filteredOrders]);

  const chartSummary = useMemo(() => {
    if (!chartData.length) return { totalOrders: 0, totalRevenue: 0 };
    return {
      totalOrders: chartData.reduce((sum, d) => sum + d.orders, 0),
      totalRevenue: chartData.reduce((sum, d) => sum + d.revenue, 0)
    };
  }, [chartData]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'shipped': return 'bg-blue-100 text-blue-700';
      case 'confirmed': case 'processing': case 'ready': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!profile || !profile.businessName) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-xl p-8 text-center text-white">
          <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-6">
            <CubeIcon className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Complete Your Supplier Profile</h2>
          <p className="text-blue-100 mb-8 max-w-md mx-auto">
            Set up your supplier profile to start selling products to farmers on Omabra.
          </p>
          <Link
            to="/supplier/settings"
            className="inline-flex items-center px-6 py-3 bg-white text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
          >
            Complete Profile
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-blue-200 text-sm mb-1">Welcome back,</p>
            <h1 className="text-2xl sm:text-3xl font-bold">
              {user?.firstName || 'Supplier'}!
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-blue-100">{profile.businessName}</span>
              {profile.isVerified && (
                <span className="inline-flex items-center px-2 py-0.5 bg-green-400/20 text-green-300 text-xs rounded-full">
                  <CheckBadgeIcon className="h-3 w-3 mr-1" />
                  Verified
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/supplier/products"
              className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Product
            </Link>
            <Link
              to="/supplier/settings"
              className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
            >
              <Cog6ToothIcon className="h-4 w-4 mr-2" />
              Settings
            </Link>
          </div>
        </div>
      </div>

      {/* Verification Status */}
      {!profile.isVerified && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start">
            <div className="p-2 bg-amber-100 rounded-lg mr-4">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-800">Verification Pending</p>
              <p className="text-sm text-amber-700 mt-1">
                Your supplier account is pending verification. Your products will be visible to farmers once approved by our team.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg shadow-blue-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-100 text-sm">Products</p>
              <p className="text-3xl font-bold mt-1">{stats?.products?.total || 0}</p>
              <p className="text-blue-200 text-xs mt-2">
                {stats?.products?.active || 0} active
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <CubeIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white shadow-lg shadow-amber-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-sm">Orders</p>
              <p className="text-3xl font-bold mt-1">{stats?.orders?.total || 0}</p>
              <p className="text-amber-200 text-xs mt-2">
                {stats?.orders?.pending || 0} pending
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <ShoppingCartIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white shadow-lg shadow-green-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-100 text-sm">Revenue</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1">
                KES {(stats?.revenue?.total || 0).toLocaleString()}
              </p>
              <p className="text-green-200 text-xs mt-2">
                All time earnings
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-5 text-white shadow-lg shadow-purple-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-100 text-sm">Pending</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1">
                KES {(stats?.revenue?.pending || 0).toLocaleString()}
              </p>
              <p className="text-purple-200 text-xs mt-2">
                Awaiting settlement
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <ClockIcon className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link to="/supplier/products" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all group">
          <div className="p-2 bg-blue-50 rounded-lg w-fit group-hover:bg-blue-100 transition-colors">
            <CubeIcon className="h-5 w-5 text-blue-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Products</p>
          <p className="text-xs text-gray-500">Manage inventory</p>
        </Link>
        <Link to="/supplier/orders" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-amber-200 transition-all group">
          <div className="p-2 bg-amber-50 rounded-lg w-fit group-hover:bg-amber-100 transition-colors">
            <TruckIcon className="h-5 w-5 text-amber-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Orders</p>
          <p className="text-xs text-gray-500">Process orders</p>
        </Link>
        <Link to="/wallet" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-green-200 transition-all group">
          <div className="p-2 bg-green-50 rounded-lg w-fit group-hover:bg-green-100 transition-colors">
            <BanknotesIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Wallet</p>
          <p className="text-xs text-gray-500">View earnings</p>
        </Link>
        <Link to="/supplier/settings" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-gray-300 transition-all group">
          <div className="p-2 bg-gray-100 rounded-lg w-fit group-hover:bg-gray-200 transition-colors">
            <Cog6ToothIcon className="h-5 w-5 text-gray-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Settings</p>
          <p className="text-xs text-gray-500">Business profile</p>
        </Link>
      </div>

      {/* Chart Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2 text-gray-400" />
                Revenue & Orders Trend
              </h2>
              <p className="text-sm text-gray-500 mt-1">Past 12 months performance</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={chartStatusFilter}
                onChange={(e) => setChartStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {ORDER_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Chart Summary */}
          <div className="flex gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-600">
                Orders: <span className="font-semibold text-gray-900">{chartSummary.totalOrders}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600">
                Revenue: <span className="font-semibold text-gray-900">KES {chartSummary.totalRevenue.toLocaleString()}</span>
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {chartData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="weekDisplay" 
                    tick={{ fontSize: 11, fill: '#6b7280' }} 
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis 
                    yAxisId="left" 
                    tick={{ fontSize: 11, fill: '#6b7280' }} 
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    formatter={(value, name) => [
                      name === 'revenue' ? `KES ${value.toLocaleString()}` : value,
                      name === 'revenue' ? 'Revenue' : 'Orders'
                    ]}
                  />
                  <Area 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fill="url(#colorRevenue)"
                    name="revenue"
                  />
                  <Area 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="orders" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    fill="url(#colorOrders)"
                    name="orders"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p>No order data yet</p>
                <p className="text-sm text-gray-400 mt-1">Orders will appear here once received</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Recent Orders</h2>
            <p className="text-sm text-gray-500">Latest orders from farmers</p>
          </div>
          <Link to="/supplier/orders" className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center">
            View All
            <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        </div>
        
        {recentOrders.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <ShoppingCartIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium">No orders yet</p>
            <p className="text-sm mt-1">Orders from farmers will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentOrders.map(order => (
              <div key={order.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className="font-medium text-gray-900">
                        #{order.orderNumber || order.id.slice(0, 8)}
                      </p>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {order.buyer?.firstName} {order.buyer?.lastName} • {order.items?.length || 0} items
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      KES {Number(order.totalAmount || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(order.placedAt || order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Support Section */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-gray-500" />
              Need Help?
            </h3>
            <p className="text-sm text-gray-600 mt-1">Our support team is here to assist you</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://wa.me/254743673899"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <PhoneIcon className="h-4 w-4 mr-2" />
              WhatsApp
            </a>
            <a
              href="mailto:omabraservices@gmail.com"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <EnvelopeIcon className="h-4 w-4 mr-2" />
              Email Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDashboard;
